import type { Commitment, DepositLimit } from '../types/commitments';
import { productOptions, type ProductOption } from './productOptions';

/** Largest whole-number part the money column holds. */
const MAX_INTEGER_DIGITS = 16;
/** Money has two decimals at most (the field itself drops a third). */
const MAX_FRACTION_DIGITS = 2;

const CAP_PATTERN = new RegExp(
  String.raw`^\d{1,${MAX_INTEGER_DIGITS}}(\.\d{1,${MAX_FRACTION_DIGITS}})?$`,
);

/** Reasons the API accepts: up to 500 characters (DepositLimitBody). */
export const DEPOSIT_LIMIT_REASON_MAX = 500;
/** Pause / resume note: up to 1000 characters (TogglePauseBody). */
export const PAUSE_REASON_MAX = 1000;

/** Digits only, no sign, no exponent, at most two decimals; the amount must be
 *  above zero. Never goes through Number(). */
export function capAmountError(raw: string): string | null {
  const v = raw.trim();
  if (v === '') return 'Enter the monthly cap.';
  if (!CAP_PATTERN.test(v)) {
    return 'Enter an amount like 5000 or 5000.50.';
  }
  if (/^0*(\.0*)?$/.test(v)) return 'The monthly cap must be above zero.';
  return null;
}

/** The same amount without insignificant zeros, so "5000.00" equals "5000". */
export function normalizeDecimal(raw: string): string {
  const v = raw.trim();
  if (!/^\d+(\.\d+)?$/.test(v)) return v;
  const [int, frac = ''] = v.split('.');
  const intPart = int.replace(/^0+(?=\d)/, '');
  // Trailing zeros, without a pattern that backtracks on a long run of them.
  let end = frac.length;
  while (end > 0 && frac[end - 1] === '0') end -= 1;
  const fracPart = frac.slice(0, end);
  return fracPart ? `${intPart}.${fracPart}` : intPart;
}

/** The cap in force today for a product: the row with no end date. */
export function activeLimitFor(limits: DepositLimit[], productId: string): DepositLimit | null {
  return limits.find((l) => l.product_id === productId && l.effective_to === null) ?? null;
}

/** True when the typed values are exactly what is already stored, so saving
 *  would only close a cap and reopen an identical one. */
export function isLimitUnchanged(
  active: DepositLimit | null,
  capInput: string,
  reasonInput: string,
): boolean {
  if (!active) return false;
  return (
    normalizeDecimal(capInput) === normalizeDecimal(active.monthly_cap) &&
    reasonInput.trim() === (active.reason ?? '').trim()
  );
}

/** Body of the PUT: the cap as typed (exact string) and a trimmed reason or null. */
export function buildLimitBody(
  capInput: string,
  reasonInput: string,
): { monthly_cap: string; reason: string | null } {
  const reason = reasonInput.trim();
  return { monthly_cap: capInput.trim(), reason: reason === '' ? null : reason };
}

/** Products a cap can be set for: those the client has a commitment on, plus
 *  any that already carry a cap. Sorted by name, one entry per product. */
export function limitProductOptions(
  commitments: Commitment[],
  limits: DepositLimit[],
): ProductOption[] {
  return productOptions(
    limits.map((l) => ({ id: l.product_id, name: l.product_name })),
    commitments.map((c) => ({ id: c.product_id, name: c.product_name })),
  );
}

/** What pausing or resuming does to what the client is expected to pay. */
export function pauseConfirmText(
  c: Pick<Commitment, 'product_name' | 'amount' | 'currency_code' | 'cadence'>,
  pause: boolean,
): string {
  const plan = `${c.amount} ${c.currency_code} ${c.cadence} on ${c.product_name}`;
  return pause
    ? `Pausing stops new expected deposits for this commitment (${plan}). The client is not expected to pay anything until you resume it. Deposits already expected stay as they are.`
    : `Resuming starts expected deposits again from today (${plan}). The client is expected to pay on the usual schedule from the next due date.`;
}

/** Pause applies to active commitments, resume to paused ones; nothing else. */
export function pauseActionFor(status: Commitment['status']): 'pause' | 'resume' | null {
  if (status === 'active') return 'pause';
  if (status === 'paused') return 'resume';
  return null;
}
